import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, ChevronDown } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "Welche Arten von Massagen bieten Sie an?",
      answer: "Wir bieten eine Vielzahl von Massagen an, darunter Entspannungsmassagen, therapeutische Massagen und spezielle Wellnessbehandlungen."
    },
    {
      question: "Wie lange dauern die Behandlungen?",
      answer: "Die Behandlungen dauern in der Regel zwischen 30 und 90 Minuten, je nach gewählter Massage."
    },
    {
      question: "Sind die Massagen für jeden geeignet?",
      answer: "Ja, unsere Massagen sind für Erwachsene jeden Alters geeignet, jedoch empfehlen wir eine vorherige Rücksprache mit uns, wenn Sie gesundheitliche Bedenken haben."
    },
    {
      question: "Wie kann ich einen Termin buchen?",
      answer: "Sie können einen Termin telefonisch oder über unser Kontaktformular auf der Website buchen."
    },
    {
      question: "Was sind Ihre Öffnungszeiten?",
      answer: "Unsere Öffnungszeiten sind Montag bis Freitag von 09:00 bis 18:00 Uhr und Samstag nach Vereinbarung."
    }
  ];

  return (
    <div className="min-h-screen bg-background grain-texture">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-4 z-50 mx-4 md:mx-8 mt-4"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 rounded-full border border-white/20 bg-white/70 backdrop-blur-xl shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
              <img 
                src="https://customer-assets.emergentagent.com/job_massage-booking-18/artifacts/up1s5d57_Screenshot_2025-12-10_at_01.58.41-removebg-preview.png"
                alt="Elysana Logo"
                className="h-12 w-auto"
              />
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="/" className="text-primary hover:text-accent transition-colors">Startseite</a>
              <a href="/all-services" className="text-primary hover:text-accent transition-colors">Alle Leistungen</a>
              <a href="/faq" className="text-accent font-medium">FAQ</a>
              <a href="/contact" className="text-primary hover:text-accent transition-colors">Kontakt</a>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* FAQ Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-primary mb-6">
              Häufig gestellte Fragen
            </h1>
            <p className="text-xl text-text-secondary">
              Hier finden Sie Antworten auf häufig gestellte Fragen. Sollten Sie weitere Fragen haben, kontaktieren Sie uns bitte.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/50 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
          >
            <p className="text-text-secondary mb-8 text-center">
              Hier finden Sie Antworten auf h\u00e4ufig gestellte Fragen. Bitte kontaktieren Sie uns, wenn Sie weitere Fragen haben.
            </p>

            <Accordion type="single" collapsible className="w-full space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-secondary/10 rounded-xl border border-secondary/20 px-6"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="text-lg font-semibold text-primary pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-text-secondary pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-center mt-12"
          >
            <p className="text-lg text-text-secondary mb-6">
              Haben Sie noch Fragen? Wir sind hier, um zu helfen!
            </p>
            <button
              onClick={() => navigate("/contact")}
              className="btn-primary"
              data-testid="contact-us-cta-btn"
            >
              Kontaktieren Sie uns
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;